import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { Config, isNotNullable, RoutingService } from '@spartacus/core'
import { filter, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators'
import {
  DataAnalyticsClickSource,
  DataAnalyticsEventLabel,
  maxQuantityFallback,
  MbConstants
} from '../../../../shared/mb-constants'
import { CartCheckoutService } from '../../../services/mb-cart-checkout.service'
import { Observable, Subscription } from 'rxjs'
import { FocusConfig, ICON_TYPE } from '@spartacus/storefront'
import { ActiveCartFacade, Cart, OrderEntry, PromotionLocation } from '@spartacus/cart/base/root'
import { MbModalService } from '../../../../shared/services/modal.service'
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'

@Component({
  selector: 'mb-added-to-cart-dialog',
  templateUrl: './mb-added-to-cart-dialog.component.html',
  styleUrls: ['./mb-added-to-cart-dialog.component.scss']
})
export class MbAddedToCartDialogComponent implements OnInit, OnDestroy {

  constants = MbConstants
  isRequestCheckoutType$: Observable<boolean> | undefined
  isMixedCart$: Observable<boolean> | undefined
  // TODO: Spartacus Upgrade - checkpoint after testing
  @Input()
  numberOfEntriesBeforeAdd: number | undefined
  @Input()
  isCartAdjusted = false

  iconTypes = ICON_TYPE

  entry$: Observable<OrderEntry | undefined> | undefined
  cart$: Observable<Cart> = this.cartService.getActive()
  loaded$: Observable<boolean> = this.cartService.isStable()
  addedEntryWasMerged$: Observable<boolean> | undefined
  promotionLocation: PromotionLocation = PromotionLocation.ActiveCart

  quantity = 0
  isModalActive: boolean | undefined
  pickupStoreName: string | undefined

  form: UntypedFormGroup = new UntypedFormGroup({})

  focusConfig: FocusConfig = {
    trap: true,
    block: true,
    autofocus: 'button',
    focusOnEscape: true,
  }

  maxItemQuantity = +(this.config.context?.maxAllowedQuantityPerProductInCart || maxQuantityFallback)

  dataAnalyticsClickSource = DataAnalyticsClickSource.CART_OVERLAY
  dataAnalyticsEventLabel = DataAnalyticsEventLabel.ADD_TO_CART

  protected quantityControl$: Observable<UntypedFormControl> | undefined

  protected subscription = new Subscription()

  constructor (protected cartService: ActiveCartFacade,
               protected routingService: RoutingService,
               protected modalService: MbModalService,
               protected cartCheckoutService: CartCheckoutService,
               protected config: Config,
               protected activeModal: NgbActiveModal) { }

  ngOnInit () {
    this.isModalActive = true
    this.addedEntryWasMerged$ = this.getAddedEntryWasMerged(this.numberOfEntriesBeforeAdd || 0)

    this.isRequestCheckoutType$ = this.cart$?.pipe(
      map(cart => this.cartCheckoutService.isRequestCheckoutType(cart)),
      shareReplay(1)
    )
    this.isMixedCart$ = this.cart$?.pipe(
      filter(isNotNullable),
      map(cart =>
        !!cart?.totalPurchaseItemsCount && cart?.totalPurchaseItemsCount > 0 &&
        !!cart?.totalRequestItemsCount && cart?.totalRequestItemsCount > 0
      ),
      shareReplay(1)
    )

    this.subscription.add(
      this.routingService
        .getRouterState()
        .pipe(filter((state) => !!state.nextState))
        .subscribe(() => this.closeDialog('dismiss'))
    )
  }

  ngOnDestroy () {
    this.subscription?.unsubscribe()
  }

  closeDialog (reason: any) {
    this.isModalActive = false
    this.activeModal.close(reason)
  }

  getTotalQuantityOfTypeOfItems (type: string, cart: Cart) {
    return this.cartCheckoutService.getTotalQuantityOfTypeOfItems(type, cart)
  }

  getQuantityControl () {
    if (!this.quantityControl$) {
      this.quantityControl$ = this.entry$?.pipe(
        filter((e) => !!e),
        map((entry) => this.getQuantityFormControl(entry)),
        switchMap(() =>
          this.form.valueChanges.pipe(
            // eslint-disable-next-line import/no-deprecated
            startWith(null),
            tap((valueChange) => {
              if (valueChange) {
                this.cartService.updateEntry(
                  valueChange.entryNumber,
                  valueChange.quantity
                )
                if (valueChange.quantity === 0) {
                  this.closeDialog('Removed')
                }
              } else {
                this.form.markAsPristine()
              }
            })
          )
        ),
        map(() => this.form.get('quantity') as UntypedFormControl),
        shareReplay({ bufferSize: 1, refCount: true })
      )
    }
    return this.quantityControl$
  }

  protected getAddedEntryWasMerged (numberOfEntriesBeforeAdd: number) {
    return this.loaded$.pipe(
      filter((loaded) => loaded),
      switchMap(() => this.cartService.getEntries()),
      map((entries) => entries.length === numberOfEntriesBeforeAdd)
    )
  }

  protected getQuantityFormControl (entry?: OrderEntry) {
    if (!this.form.get('quantity')) {
      const quantity = new UntypedFormControl(entry?.quantity, {
        updateOn: 'blur',
      })
      this.form.addControl('quantity', quantity)

      const entryNumber = new UntypedFormControl(entry?.entryNumber)
      this.form.addControl('entryNumber', entryNumber)
    } else {
      // set the real quantity added to cart
      this.form.get('quantity')?.setValue(entry?.quantity)
    }

    return this.form.get('quantity') as UntypedFormControl
  }
}
